import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { onTenantSuspended } from '@/shared/api/httpClient';
import { MarkTile } from '@/shared/ui';

import styles from './SuspendedGate.module.css';

/**
 * Pełnoekranowy komunikat „konto firmy jest wstrzymane" (dec. 143).
 *
 * ⭐ **Dlaczego przejmuje CAŁY ekran, a nie jest toastem:** to nie jest odmowa jednej
 * operacji, tylko stan całej firmy — każde następne żądanie skończy się tak samo.
 * Aplikacja pod spodem pokazywałaby dane z pamięci podręcznej i wyglądała na żywą,
 * a każde kliknięcie kończyłoby się cichym błędem.
 *
 * ⚠ Sygnał przychodzi z <c>httpClient</c>, nie z pojedynczego ekranu: kod `403
 * tenant.suspended` może przyjść z DOWOLNEGO zapytania, także z tła.
 *
 * ⛔ Bez wyjścia w postaci „spróbuj ponownie": wznowić może wyłącznie operator, więc
 * przycisk, który niczego nie naprawia, byłby obietnicą bez pokrycia. Zostaje odświeżenie
 * strony — jedyna rzecz, która ma sens, gdy sprawa zostanie już załatwiona.
 */
export function SuspendedGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [wstrzymany, setWstrzymany] = useState(false);

  useEffect(() => onTenantSuspended(() => setWstrzymany(true)), []);

  if (!wstrzymany) return children;

  return (
    <main className={styles['page']}>
      <div className={styles['card']}>
        <div className={styles['brand']}>
          <MarkTile size={32} />
          <span className={styles['name']}>{t('app.brand')}</span>
        </div>

        <h1 className={styles['title']}>{t('app.suspended.title')}</h1>
        <p className={styles['lead']}>{t('app.suspended.lead')}</p>
        <p className={styles['note']}>{t('app.suspended.note')}</p>

        <button className={styles['retry']} type="button" onClick={() => window.location.reload()}>
          {t('app.suspended.refresh')}
        </button>
      </div>
    </main>
  );
}
